import { isNil } from 'lodash-es';
/**
 * Represents a tree node with an optional parent and a list of children.
 * @export
 */
export class TreeNode {
    /**
     * Creates a new TreeNode instance.
     *
     * @param {string} id - The unique identifier of the tree node.
     * @param {number | undefined} childCount - The count of children this tree node has or undefined.
     * @param {() => number} orderIndex - A function that returns the index of the tree node among its siblings.
     * @param {TreeNode | undefined} parent - The parent node of this tree node, if any.
     */
    constructor(id, childCount, orderIndex, parent) {
        var _a;
        this.id = id;
        this.childCount = childCount;
        this.orderIndex = orderIndex;
        this._dataIdSequence = 0;
        /**
         * The children nodes of this tree node.
         * @readonly
         */
        this.children = [];
        this.data = [];
        this.dataUpdateListeners = [];
        this.dataRemovalListeners = [];
        this.dataAdditionListeners = [];
        this.parent = parent;
        this.level = ((_a = parent === null || parent === void 0 ? void 0 : parent.level) !== null && _a !== void 0 ? _a : -1) + 1;
    }
    addData(dataAttributes) {
        const dataId = this.nextDataId();
        this.data.push(new TreeNodeData(dataId, this.id, dataAttributes.rowPosition, dataAttributes.columnPosition));
        this.notifyAdditionListeners(dataId);
        return dataId;
    }
    notifyAdditionListeners(dataId) {
        this.dataAdditionListeners.forEach(listener => listener(dataId));
    }
    registerDataAdditionListener(listener) {
        if (this.dataAdditionListeners.indexOf(listener) === -1) {
            this.dataAdditionListeners.push(listener);
        }
        else {
            throw new Error('Listener already registered.');
        }
    }
    removeDataAdditionListener(listener) {
        const index = this.dataAdditionListeners.indexOf(listener);
        if (index !== -1) {
            this.dataAdditionListeners.splice(index, 1);
        }
        else {
            throw new Error('Listener not registered.');
        }
    }
    removeData(dataId) {
        const index = this.data.findIndex(data => data.id === dataId);
        if (index !== -1) {
            const [removed] = this.data.splice(index, 1);
            this.notifyRemovalListeners(removed);
        }
        else {
            throw new Error(`Data with id ${dataId} not found.`);
        }
    }
    registerDataRemovalListener(listener) {
        if (this.dataRemovalListeners.indexOf(listener) === -1) {
            this.dataRemovalListeners.push(listener);
        }
        else {
            throw new Error('Listener already registered.');
        }
    }
    removeDataRemovalListener(listener) {
        const index = this.dataRemovalListeners.indexOf(listener);
        if (index !== -1) {
            this.dataRemovalListeners.splice(index, 1);
        }
        else {
            throw new Error('Listener not registered.');
        }
    }
    notifyRemovalListeners(data) {
        this.dataRemovalListeners.forEach(listener => listener(data));
    }
    getData(dataId) {
        const data = this.data.find(data => data.id === dataId);
        if (data) {
            return data;
        }
        else {
            throw new Error(`Data with id ${dataId} not found.`);
        }
    }
    updateData(dataId, dataAttributes) {
        const data = this.getData(dataId);
        data.rowPosition = dataAttributes.rowPosition;
        data.columnPosition = dataAttributes.columnPosition;
        this.notifyUpdateListeners(dataId);
    }
    registerDataUpdateListener(listener) {
        if (this.dataUpdateListeners.indexOf(listener) === -1) {
            this.dataUpdateListeners.push(listener);
        }
        else {
            throw new Error('Listener already registered.');
        }
    }
    removeDataUpdateListener(listener) {
        const index = this.dataUpdateListeners.indexOf(listener);
        if (index !== -1) {
            this.dataUpdateListeners.splice(index, 1);
        }
        else {
            throw new Error('Listener not registered.');
        }
    }
    notifyUpdateListeners(dataId) {
        this.dataUpdateListeners.forEach(listener => listener(dataId));
    }
    nextDataId() {
        return `node-data-${this.id}-${this._dataIdSequence++}`;
    }
    /**
     * Returns the actual count of children for this tree node. If `childCount` is not specified,
     * it returns the current number of children. If `childCount` is specified,
     * it returns the maximum of `childCount` and the current number of children.
     *
     * @private
     * @returns {number} - The actual count of children.
     */
    actualChildCount() {
        return isNil(this.childCount)
            ? this.children.length
            : Math.max(this.childCount, this.children.length);
    }
    /**
     * Indicates whether this tree node is the last child of its parent.
     *
     * @property {boolean} isLastChild - True if this tree node is the last child of its parent, false otherwise.
     * @readonly
     */
    get isLastChild() {
        if (this.parent === undefined) {
            return true;
        }
        else {
            return this.orderIndex() === this.parent.actualChildCount() - 1;
        }
    }
    static firstDefinedOrCalculation(arg1, arg2, calculator) {
        if (arg1 === undefined) {
            return arg2;
        }
        else if (arg2 === undefined) {
            return arg1;
        }
        else {
            return calculator(arg1, arg2);
        }
    }
    calculatePositions() {
        return this.data.reduce((acc, curr) => {
            return {
                rowPosition: TreeNode.firstDefinedOrCalculation(acc.rowPosition, curr.rowPosition, (accRow, currRow) => {
                    return {
                        start: Math.min(accRow.start, currRow.start),
                        end: Math.max(accRow.end, currRow.end),
                    };
                }),
                colPosition: TreeNode.firstDefinedOrCalculation(acc.colPosition, curr.columnPosition, (accCol, currCol) => {
                    return {
                        start: Math.min(accCol.start, currCol.start),
                        end: Math.max(accCol.end, currCol.end),
                    };
                }),
            };
        }, {
            rowPosition: undefined,
            colPosition: undefined,
        });
    }
}
export class TreeNodeData {
    constructor(id, parentId, rowPosition, columnPosition) {
        this.id = id;
        this.parentId = parentId;
        this.rowPosition = rowPosition;
        this.columnPosition = columnPosition;
    }
}
/**
 * Manages the state of a hierarchical tree of nodes.
 * Provides methods to add, update, and remove nodes, as well as to register listeners for these actions.
 * @export
 */
export class HierarchicalTreeStateService {
    constructor() {
        this.nodeIndex = new Map();
        this.additionListeners = [];
        this.childAdditionListenersByParent = new Map();
        this.removalListeners = [];
        this.updateListeners = [];
        this._idSequence = 0;
    }
    /**
     * Generates a new unique node ID.
     * @private
     * @returns {string} - A unique node ID.
     */
    nextId() {
        return `node_${this._idSequence++}`;
    }
    /**
     * Adds a root node to the tree.
     * @public
     * @returns {string} - The ID of the added root node.
     */
    addRootNode() {
        const id = this.nextId();
        const node = new TreeNode(id, undefined, () => 0, undefined);
        this.nodeIndex.set(id, node);
        return id;
    }
    removeRootNode(nodeId) {
        const node = this.nodeIndex.get(nodeId);
        if (node !== undefined) {
            this.purgeNode(node);
        }
    }
    /**
     * Retrieves a node with the given ID.
     * @public
     * @param {string} id - The ID of the node to retrieve.
     * @returns {TreeNode | undefined} - The node with the given ID or undefined if not found.
     */
    getNode(id) {
        return this.nodeIndex.get(id);
    }
    /**
     * Adds a new node to the tree as a child of the specified parent node.
     * @public
     * @param {string} parentId - The ID of the parent node to which the new node will be added.
     * @param {TreeNodeAttributes} {orderIndex, childCount} - The attributes of the new node.
     * @returns {string} - The ID of the newly added node.
     * @throws {Error} - If the parent node is not found in the tree.
     */
    addNode(parentId, { orderIndex, childCount }) {
        const parent = this.nodeIndex.get(parentId);
        if (parent === undefined) {
            throw new Error(`No node found for id ${parentId}`);
        }
        else {
            const id = this.nextId();
            const node = new TreeNode(id, childCount, orderIndex, parent);
            this.nodeIndex.set(id, node);
            parent.children.push(node);
            this.notifyNodeAddition(id, parentId);
            return id;
        }
    }
    /**
     * Updates the attributes of the specified node.
     * If the node has a parent, the node is removed from its parent's children list and replaced with the updated node.
     * The children of the original node are added to the updated node.
     * @public
     * @param {string} id - The ID of the node to update.
     * @param {TreeNodeAttributes} {orderIndex, childCount} - The new attributes of the node.
     * @throws {Error} - If the specified node is not found in the tree.
     */
    updateNode(id, { orderIndex, childCount }) {
        const existing = this.nodeIndex.get(id);
        if (existing === undefined) {
            throw new Error(`No node found for id ${id}`);
        }
        const parent = existing === null || existing === void 0 ? void 0 : existing.parent;
        const node = new TreeNode(id, childCount, orderIndex, parent);
        if (parent !== undefined) {
            this.replaceNodeInParent(parent, existing, node);
        }
        this.nodeIndex.set(id, node);
        node.children.push(...existing.children);
        existing.children.length = 0;
        if (parent !== undefined) {
            // do not notify if root node
            this.notifyNodeUpdate(id, parent.id);
        }
    }
    /**
     * Replaces a node in its parent's children list with a new node.
     * @private
     * @param {TreeNode} parent - The parent node containing the node to replace.
     * @param {TreeNode} oldNode - The node to be replaced.
     * @param {TreeNode} newNode - The replacement node.
     * @throws {Error} - If the old node is not found in the parent's children list.
     */
    replaceNodeInParent(parent, oldNode, newNode) {
        const index = parent.children.indexOf(oldNode);
        if (index === -1) {
            throw new Error(`Node not found in parent's children`);
        }
        else {
            parent.children[index] = newNode;
        }
    }
    /**
     * Removes the node with the specified ID from the tree.
     * If the node has children, they are also removed.
     * @public
     * @param {string} id - The ID of the node to remove.
     * @throws {Error} - If the node is not found in the tree, or if the node is the root node.
     */
    removeNode(id) {
        const node = this.nodeIndex.get(id);
        if (node !== undefined) {
            const parent = node.parent;
            if (parent === undefined) {
                throw new Error(`Cannot remove root node`);
            }
            else {
                const index = parent.children.indexOf(node);
                if (index === -1) {
                    throw new Error(`Node ${id} not found in parent's children`);
                }
                else {
                    parent.children.splice(index, 1);
                    this.purgeNode(node);
                }
            }
        }
    }
    purgeNode(node) {
        this.nodeIndex.delete(node.id);
        node.children.forEach(child => this.removeNode(child.id));
        this.notifyNodeRemoval(node);
    }
    /**
     * Registers a listener to be called when a node is added to the tree.
     * @public
     * @param {(id: string) => void} listener - The listener function to be called with the ID of the added node.
     * @param doNotFail - If true, the listener will not throw an error if it is already registered.
     * @returns {void}
     * @throws {Error} - If the listener is already registered.
     */
    addNodeAdditionListener(listener, doNotFail = false) {
        if (this.additionListeners.indexOf(listener) === -1) {
            this.additionListeners.push(listener);
        }
        else if (!doNotFail) {
            throw new Error(`Listener already registered`);
        }
    }
    /**
     * Removes a previously registered listener for node addition events.
     * @public
     * @param {(id: string) => void} listener - The listener function to be removed.
     * @returns {void}
     */
    removeNodeAdditionListener(listener) {
        const index = this.additionListeners.indexOf(listener);
        if (index !== -1) {
            this.additionListeners.splice(index, 1);
        }
    }
    /**
     * Registers a listener that fires only for node additions whose parentId matches the given id.
     * Avoids the O(N) global dispatch of addNodeAdditionListener for per-node child handlers.
     */
    addChildAdditionListener(parentId, listener) {
        let bucket = this.childAdditionListenersByParent.get(parentId);
        if (bucket === undefined) {
            bucket = [];
            this.childAdditionListenersByParent.set(parentId, bucket);
        }
        if (bucket.indexOf(listener) === -1) {
            bucket.push(listener);
        }
    }
    removeChildAdditionListener(parentId, listener) {
        const bucket = this.childAdditionListenersByParent.get(parentId);
        if (bucket === undefined) {
            return;
        }
        const index = bucket.indexOf(listener);
        if (index !== -1) {
            bucket.splice(index, 1);
        }
        if (bucket.length === 0) {
            this.childAdditionListenersByParent.delete(parentId);
        }
    }
    notifyNodeAddition(id, parentId) {
        for (const listener of this.additionListeners) {
            listener(id, parentId);
        }
        const bucket = this.childAdditionListenersByParent.get(parentId);
        if (bucket !== undefined) {
            for (const listener of bucket) {
                listener(id, parentId);
            }
        }
    }
    /**
     * Registers a listener to be called when a node is removed from the tree.
     * @public
     * @param {(node: TreeNode) => void} listener - The listener function to be called with the removed TreeNode.
     * @param doNotFail - If true, the listener will not throw an error if it is already registered.
     * @returns {void}
     * @throws {Error} - If the listener is already registered.
     */
    addNodeRemovalListener(listener, doNotFail = false) {
        if (this.removalListeners.indexOf(listener) === -1) {
            this.removalListeners.push(listener);
        }
        else if (!doNotFail) {
            throw new Error(`Listener already registered`);
        }
    }
    /**
     * Removes a previously registered listener for node removal events.
     * @public
     * @param {(node: TreeNode) => void} listener - The listener function to be removed.
     * @returns {void}
     */
    removeNodeRemovalListener(listener) {
        const index = this.removalListeners.indexOf(listener);
        if (index !== -1) {
            this.removalListeners.splice(index, 1);
        }
    }
    notifyNodeRemoval(node) {
        for (const listener of this.removalListeners) {
            listener(node);
        }
    }
    /**
     * Registers a listener to be called when a node is updated in the tree.
     * @public
     * @param {(id: string, parentId: string) => void} listener - The listener function to be called with the ID of the updated node and the ID of its parent.
     * @returns {void}
     * @throws {Error} - If the listener is already registered.
     */
    addNodeUpdateListener(listener) {
        if (this.updateListeners.indexOf(listener) === -1) {
            this.updateListeners.push(listener);
        }
        else {
            throw new Error(`Listener already registered`);
        }
    }
    /**
     * Removes a previously registered listener for node update events.
     * @public
     * @param {(id: string) => void} listener - The listener function to be removed.
     * @returns {void}
     */
    removeNodeUpdateListener(listener) {
        const index = this.updateListeners.indexOf(listener);
        if (index !== -1) {
            this.updateListeners.splice(index, 1);
        }
    }
    notifyNodeUpdate(id, parentId) {
        for (const listener of this.updateListeners) {
            listener(id, parentId);
        }
    }
}
export function isTreeViewNode(obj) {
    return (obj === null || obj === void 0 ? void 0 : obj.nodeOrderInView) !== undefined;
}
/**
 * Generator function that yields the path from the given node to its root's grandchild,
 * excluding nodes that do not have a grandparent.
 * @export
 * @param {TreeNode} node - The starting node for the path.
 * @returns {Generator<TreeNode, void>} - A generator that yields each TreeNode in the path.
 */
export function* pathToRootsGrandchild(node) {
    if (isNil(node) || isNil(node.parent) || isNil(node.parent.parent)) {
        return;
    }
    else {
        yield* pathToRootsGrandchild(node.parent);
        yield node;
    }
}
